import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, thumbUrl, formatApiError } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { DiscountBox } from "@/components/DiscountBox";
import { toast } from "sonner";

function formatPrice(p, currency = "gbp") {
  try {
    return new Intl.NumberFormat("en-GB", { style: "currency", currency: currency.toUpperCase() }).format(p);
  } catch {
    return `£${Number(p).toFixed(2)}`;
  }
}

export default function CartPage() {
  const { items, total, currency, mixedCurrency, removeItem, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [discount, setDiscount] = useState(null);
  const payable = discount ? discount.total_after : total;

  useEffect(() => {
    if (user?.email) setEmail(user.email);
  }, [user]);

  const checkout = async () => {
    if (items.length === 0) return;
    if (!email) { toast.error("Enter your email so we can deliver the downloads."); return; }
    if (mixedCurrency) { toast.error("Your basket has mixed currencies. Please remove items until they all match."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/checkout/cart", {
        image_ids: items.map((it) => it.id),
        origin_url: window.location.origin,
        buyer_email: email,
        discount_code: discount?.code || null,
      });
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error("Could not start checkout.");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-6 pt-24 pb-32 text-center" data-testid="cart-page-empty">
        <p className="text-xs uppercase tracking-[0.4em] pm-orange mb-6 font-semibold">Basket</p>
        <h1 className="font-display text-6xl uppercase leading-none">Empty</h1>
        <p className="text-white/60 font-condensed mt-6">Add photos to your basket to buy several at once.</p>
        <Link to="/" className="pm-btn inline-block mt-10" data-testid="cart-back-home">
          Browse events
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 mt-8 pb-24" data-testid="cart-page">
      <div className="border-b-2 border-[var(--pm-orange-40)] pb-6 mb-10 flex items-end justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] pm-orange font-semibold">Basket</p>
          <h1 className="font-display text-5xl md:text-6xl uppercase mt-2">
            {items.length} <span className="pm-orange">{items.length === 1 ? "photo" : "photos"}</span>
          </h1>
        </div>
        <button
          onClick={() => { if (window.confirm("Clear your basket?")) clearCart(); }}
          className="text-xs uppercase tracking-[0.25em] text-white/50 hover:text-red-400"
          data-testid="cart-clear"
        >
          Clear basket
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-10">
        {/* Items */}
        <div className="space-y-3">
          {items.map((it) => (
            <div
              key={it.id}
              className="flex items-center gap-5 border-2 border-white/10 bg-[#0a0a0a] p-3 hover:border-[var(--pm-orange-40)] transition"
              data-testid={`cart-item-${it.id}`}
            >
              <Link to={`/image/${it.id}`} className="shrink-0">
                <img src={thumbUrl(it.id)} alt={it.title} className="w-28 h-28 object-cover" />
              </Link>
              <div className="flex-1 min-w-0">
                <Link to={`/image/${it.id}`} className="font-display text-2xl uppercase leading-tight hover:pm-orange truncate block">
                  {it.title}
                </Link>
                <p className="text-xs uppercase tracking-[0.25em] text-white/40 font-condensed mt-1">{it.currency.toUpperCase()}</p>
              </div>
              <div className="font-display text-2xl uppercase text-right">
                {discount?.per_image?.[it.id] > 0 ? (
                  <>
                    <span className="block text-sm text-white/40 line-through" data-testid={`cart-item-list-${it.id}`}>{formatPrice(it.price, it.currency)}</span>
                    <span className="pm-orange" data-testid={`cart-item-final-${it.id}`}>{formatPrice(Math.max(0, it.price - discount.per_image[it.id]), it.currency)}</span>
                  </>
                ) : formatPrice(it.price, it.currency)}
              </div>
              <button
                onClick={() => removeItem(it.id)}
                className="text-xs uppercase tracking-[0.25em] text-white/50 hover:text-red-400 px-2"
                data-testid={`cart-remove-${it.id}`}
                aria-label="Remove"
              >
                Remove
              </button>
            </div>
          ))}
        </div>

        {/* Summary */}
        <aside className="lg:sticky lg:top-24 self-start space-y-6 border-2 border-[var(--pm-orange-40)] p-6 bg-[#0a0a0a]" data-testid="cart-summary">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] pm-orange font-semibold">Summary</p>
            <div className="mt-4 flex items-baseline justify-between border-b border-white/10 pb-4">
              <span className="text-sm text-white/60 font-condensed">Photos</span>
              <span className="font-display text-2xl uppercase">{items.length}</span>
            </div>
            {discount && !mixedCurrency && (
              <>
                <div className="mt-3 flex items-baseline justify-between">
                  <span className="text-sm text-white/60 font-condensed">Subtotal</span>
                  <span className="font-display text-xl uppercase text-white/60" data-testid="cart-subtotal">{formatPrice(total, currency)}</span>
                </div>
                <div className="mt-1 flex items-baseline justify-between">
                  <span className="text-sm pm-orange font-condensed">Discount · {discount.code}</span>
                  <span className="font-display text-xl uppercase pm-orange" data-testid="cart-discount">−{formatPrice(discount.discount_total, currency)}</span>
                </div>
              </>
            )}
            <div className="mt-3 flex items-baseline justify-between">
              <span className="text-sm text-white/60 font-condensed">Total</span>
              <span className="font-display text-4xl uppercase" data-testid="cart-total">
                {mixedCurrency ? "mixed" : formatPrice(payable, currency)}
              </span>
            </div>
            {mixedCurrency && (
              <p className="text-xs text-red-400 mt-3 font-condensed">Your basket has items priced in different currencies. Remove until only one currency remains.</p>
            )}
          </div>

          {!mixedCurrency && (
            <DiscountBox imageIds={items.map((it) => it.id)} currency={currency} applied={discount} onApplied={setDiscount} />
          )}

          <div>
            <label className="text-xs uppercase tracking-[0.3em] pm-orange block mb-2 font-semibold">Email for delivery</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="input-minimal"
              data-testid="cart-email"
            />
          </div>

          <button
            onClick={checkout}
            disabled={busy || mixedCurrency}
            className="pm-btn w-full text-center"
            data-testid="cart-checkout"
          >
            {busy ? "Starting checkout…" : `Checkout · ${mixedCurrency ? "—" : formatPrice(payable, currency)}`}
          </button>

          <button
            onClick={() => navigate(-1)}
            className="text-xs uppercase tracking-[0.25em] text-white/50 hover:text-white w-full text-center"
            data-testid="cart-continue"
          >
            ← Continue shopping
          </button>

          <p className="text-xs text-white/40 font-condensed leading-relaxed">
            Instant download of every photo after payment. Full-resolution, unwatermarked files delivered to your email.
          </p>
        </aside>
      </div>
    </div>
  );
}
